import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Bell, Loader2, MessageSquare, RefreshCw } from 'lucide-react';
import { Card, PageHeader, Button } from '../components/ui';
import { dashboardApi, type Business, type BusinessOverview } from '../lib/api';
import { getBusinessList } from '../lib/businessListCache';
import { buildNotifications } from '../utils/notifications';

export function Notifications() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [overview, setOverview] = useState<BusinessOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const [businessesData, overviewRes] = await Promise.all([
        getBusinessList(),
        dashboardApi.getOverview(),
      ]);

      setBusinesses(businessesData);
      setOverview(overviewRes.data.data);
    } catch (e) {
      console.error(e);
      setError('Не удалось загрузить уведомления');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const notifications = useMemo(
    () => buildNotifications(businesses, overview),
    [businesses, overview],
  );

  return (
    <div>
      <PageHeader
        title="Уведомления"
        subtitle={`${notifications.length} активных`}
      />

      <div className="p-6 space-y-4">
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            icon={<RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />}
            onClick={loadNotifications}
          >
            Обновить
          </Button>
        </div>

        {loading ? (
          <Card>
            <div className="py-10 flex items-center justify-center gap-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              Загружаем уведомления...
            </div>
          </Card>
        ) : error ? (
          <Card>
            <div className="py-4 text-sm text-red-600">{error}</div>
          </Card>
        ) : notifications.length === 0 ? (
          <Card>
            <div className="py-10 text-center">
              <Bell className="w-10 h-10 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">Новых уведомлений пока нет</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => navigate(`/client/${item.businessId}/dashboard`)}
                className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                  item.type === 'reviews'
                    ? 'border-blue-200 bg-blue-50 hover:bg-blue-100'
                    : 'border-amber-200 bg-amber-50 hover:bg-amber-100'
                }`}
              >
                <div className="flex items-start gap-3">
                  {item.type === 'reviews' ? (
                    <MessageSquare className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-700 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
